<template>
  <div class="decision-component" v-click-away="validateDecision">
    <IslandBadge type="decision"></IslandBadge>
    <div class="component-contents">
      <Draggable
        v-model="decisions"
        class="component-contents__decisions-list"
        @end="updateDecisionsIfChanged"
        handle=".handle"
        item-key="key"
      >
        <template #item="{ element: decision, index }">
          <div :key="createKey(decision)">
            <Decision
              ref="decisionComponents"
              class="component-contents__decision"
              :decision="decision"
              :canDelete="decisions.length > 1"
              @deleted="deleteDecision(index)"
              @updated="(updatedDecision: DecisionViewModel) => updateDecision(index, updatedDecision)"
            ></Decision>
          </div>
        </template>
      </Draggable>
      <a @click="addDecision" class="component-contents__add-condition-button">
        <AddIcon />
        <span class="component-contents__add-condition-label">Add decision</span>
      </a>

      <div class="component-contents__else-block">
        <div class="component-contents__else">
          <TagBox value="else" />
          <div class="component-contents__else-transition-picker">
            <TransitionPicker :transition="state.noMatch" @transition-selected="updateNoMatch"></TransitionPicker>
            <p v-if="isNoMatchPayloadInvalid" class="help is-danger">Must not contain an invalid transition</p>
            <p v-else-if="isNoMatchRequiredInvalid" class="help is-danger">Must select a transition</p>
          </div>
        </div>
      </div>
    </div>

    <div class="invalid-warning" v-if="invalid">
      <ErrorIcon class="icon-1-3x"></ErrorIcon>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Draggable from 'vuedraggable';
import { StateViewModel } from '@/features/bots/models/designer';
import IslandBadge from './IslandBadge.vue';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import TransitionPicker from '@/features/bots/components/designer/TransitionPicker.vue';
import TagBox from '@/shared/components/TagBox.vue';
import { DecisionViewModel } from '@/features/bots/models/designer/decisionViewModel';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import Decision from '@/features/bots/components/designer/Decision.vue';
import { required } from '@vuelidate/validators';
import { nanoid } from 'nanoid';
import { computed, nextTick, onMounted, PropType, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { useStore } from 'vuex';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  value: {
    type: Object as PropType<StateViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'state-updated', value: StateViewModel): void;
}>();

const state = ref<StateViewModel>({
  id: '',
  component: 'decision',
  decisions: [],
});
const decisions = ref<DecisionViewModel[]>([]);
const invalid = ref(true);
const decisionComponents = ref<Array<typeof Decision>>([]);

const validations = {
  state: {
    noMatch: {
      required,
      type: {
        required,
      },
      payload: {
        payloadValid(payload: string) {
          if (payload) {
            return !isTransitionPayloadInvalid(payload).value;
          }
          return true;
        },
      },
    },
  },
};

const $v = useVuelidate(validations, { state });
const isNoMatchRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.state.noMatch));
const isNoMatchPayloadInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.state.noMatch, 'payloadValid'),
);

const store = useStore();
const isTransitionPayloadInvalid = (payload: string) =>
  computed(() => store.getters.isTransitionPayloadInvalid(payload));

onMounted(() => {
  if (props.value && props.value.decisions) {
    state.value = props.value;
    decisions.value = state.value.decisions || [];
    invalid.value = state.value.invalid === undefined ? false : state.value.invalid;
  }
});

function addDecision() {
  const decision = {
    transition: {
      type: undefined,
      payload: undefined,
    },
    conditions: {
      type: 'and',
      conditions: [],
    },
  } as DecisionViewModel;
  decisions.value.push(decision);
  nextTick(() => decisionsChanged());
}

function deleteDecision(index: number) {
  if (decisions.value.length === 1) {
    return;
  }
  decisions.value.splice(index, 1);
  nextTick(() => decisionsChanged());
}

function updateDecision(index: number, decision: DecisionViewModel) {
  decisions.value[index] = decision;
  decisionsChanged();
}

function updateDecisionsIfChanged() {
  if (state.value.decisions !== decisions.value) {
    decisionsChanged();
  }
}

function updateNoMatch(noMatch: TransitionViewModel) {
  state.value.noMatch = noMatch;
  state.value.decisions = decisions.value;
  emitUpdatedEvent();
}

function validateDecision() {
  $v.value.$touch();
}

function createKey(decision: DecisionViewModel) {
  if (!decision.key) {
    decision.key = nanoid(7);
  }
  return decision.key;
}

function decisionsChanged() {
  state.value.decisions = decisions.value;
  emitUpdatedEvent();
}

function emitUpdatedEvent() {
  invalid.value = $v.value.$invalid;
  state.value.invalid = invalid.value;
  emit('state-updated', state.value);
}
</script>

<style scoped lang="scss">
.decision-component {
  display: flex;
  flex: 1;
  position: relative;
}

.component-contents {
  flex: 1;
  display: flex;
  position: relative;
  flex-direction: column;

  &__decisions-list {
    padding: 10px 5px 5px;
  }

  &__decision {
    margin-top: 10px;
  }

  &__add-condition-button {
    margin-top: 10px;
    align-self: flex-start;
  }

  &__add-condition-label {
    margin-left: 0.25em;
  }

  &__else {
    position: relative;
    display: flex;
    flex-direction: row;
    margin-top: 10px;
    border: 1px rgb(226, 226, 226) solid;
    border-radius: 0.25rem;
    padding: 20px 40px 20px 20px;
    gap: 30px;
    margin-bottom: 20px;
  }

  &__else-block {
    padding: 10px 5px 5px;
  }

  &__else-transition-picker {
    width: 100%;
  }

  :deep(.transition-picker) {
    .dropdown {
      width: 100px;
    }
  }
}
</style>
